import type { CoursesV1, OnDemandSpecializationsV1 } from '__generated__/graphql-types-do-not-use';

import { languageCodeToName } from 'js/lib/language';

import type { ConstantProductCardCourseFragmentFragment } from 'bundles/enterprise-collections/queries/fragments/__generated__/ConstantProductCardCourseFragment';
import type { ConstantProductCardS12nFragmentFragment } from 'bundles/enterprise-collections/queries/fragments/__generated__/ConstantProductCardS12nFragment';
import type {
  FilterKeys,
  FilterState,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/useProductFilterState';
import { filterKeys as productFilterKeys } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/useProductFilterState';

import _t from 'i18n!nls/program-skills-objectives';

export type CourseElement = Pick<CoursesV1, 'primaryLanguages' | 'subtitleLanguages' | 'level' | 'domainTypes'> &
  ConstantProductCardCourseFragmentFragment;
export type S12nElement = Pick<OnDemandSpecializationsV1, 'productVariant' | 'metadata' | 'domainTypes' | 'courses'> &
  ConstantProductCardS12nFragmentFragment;

export type ProductItem = {
  isCourse: boolean;
  product: CourseElement | S12nElement;
  id: string;
};

export type FilterDropdownOptionsMap = Record<FilterKeys, { value: string; label: string }[]>;

const domainIdToName = (domainId: string) => {
  const domainMap = {
    'arts-and-humanities': _t('Arts and Humanities'),
    business: _t('Business'),
    'computer-science': _t('Computer Science'),
    'data-science': _t('Data Science'),
    'information-technology': _t('Information Technology'),
    'life-sciences': _t('Life Sciences'),
    'math-and-logic': _t('Math and Logic'),
    'personal-development': _t('Personal Development'),
    'physical-science-and-engineering': _t('Physical Science and Engineering'),
    'social-sciences': _t('Social Sciences'),
    'language-learning': _t('Language Learning'),
  };

  return domainMap[domainId as keyof typeof domainMap];
};

// Removes hyphens and capitalizes the first letter of each word
// EG. computer-science -> Computer Science
export const mapDomainIdToReadableName = (domainId: string): string => {
  if (!domainId) {
    return '';
  }

  const translatedDomain = domainIdToName(domainId);

  if (translatedDomain) {
    return translatedDomain;
  } else {
    const stringWithoutHyphens = domainId.replace(/-/g, ' ');
    const words = stringWithoutHyphens.split(' ');
    const capitalizedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1));

    return capitalizedWords.join(' ');
  }
};

// Lowercase the string and capitalizes the first letter
// E.G. INTERMEDIATE -> Intermediate
export const mapLevelToReadableName = (level: string): string => {
  if (!level) {
    return '';
  }
  const lowercaseLevel = level.toLowerCase();
  return lowercaseLevel.charAt(0).toUpperCase() + lowercaseLevel.slice(1);
};

// Maps course metadata to static readable string
export const getReadableCourseType = (courseType: string) => {
  if (courseType === 'CourseTypeMetadataV1_standardCourseMember') {
    // eslint-disable-next-line no-restricted-syntax
    return _t('Standard Course');
  } else if (courseType === 'CourseTypeMetadataV1_projectMember') {
    return _t('Project');
  } else if (courseType === 'CourseTypeMetadataV1_rhymeProjectMember') {
    return _t('Guided Project');
  } else if (courseType === 'NormalS12n') {
    return _t('Specialization');
  } else if (courseType === 'ProfessionalCertificateS12n') {
    return _t('Professional Certificate');
  } else {
    return _t('Unknown');
  }
};

export const mapToDropdownOption = ([key, value]: [string, string]) => ({
  value: key,
  label: value,
});

/**
 * This function loops over a list of courses and s12ns and aggregates the values for the following filters:
 * - Subject/domain
 * - Level
 * - Language
 * - Product Type
 * - Partner
 *
 * It then maps these values to dropdown options which are returned.
 * This is used in the filters in the skills page so we only show filter values that have at least one course/s12n associated with them.
 *
 * We only generate values for the filters if there is not an active filter for that filter key.
 *
 * Unfortunately the logic for this is a bit convoluted.
 * It's also worth noting that we chose to filter these on the FE because the api called does not support filtering and paging.
 * Implementing the filtering and paging would be quite expensive and because the api calls the skills recs endpoint which also does not support
 * filtering and paging it gives no advantage and just adds an additional network request.
 */

export const generateFilterDropdownOptions = (products: ProductItem[], filterState: FilterState) => {
  // Aggregate all possible filters based on api results
  const filterDropdownOptionsMap = products.reduce(
    (dropdownOptions, product) => {
      /* eslint-disable no-param-reassign */
      if (product.isCourse) {
        const course = product.product as CourseElement;
        // Add an option for each unique primary language
        [...(course?.primaryLanguages || [])].forEach((language) => {
          // If we are filtering on a language and this language is not the one we are filtering on, skip it
          if (!language) return;

          if (!(filterState[productFilterKeys.LANGUAGE] && filterState[productFilterKeys.LANGUAGE] !== language)) {
            dropdownOptions[productFilterKeys.LANGUAGE][language] = languageCodeToName(language);
          }
        });

        // add an option for the course level
        // If its an active filter only add the course level which is active
        if (
          course.level &&
          !(filterState[productFilterKeys.LEVEL] && filterState[productFilterKeys.LEVEL] !== course.level)
        ) {
          dropdownOptions[productFilterKeys.LEVEL][course.level] = mapLevelToReadableName(course.level);
        }
        // add an option for each unique partner
        // If its an active filter only add the partner which is active
        (course?.partners?.elements || []).forEach((partner) => {
          if (
            partner &&
            !(filterState[productFilterKeys.PARTNER] && filterState[productFilterKeys.PARTNER] !== partner.id)
          ) {
            dropdownOptions[productFilterKeys.PARTNER][partner.id] = partner.name;
          }
        });
        // Add an option for the courseType
        // If its an active filter only add the courseType which is active
        const courseType = course.courseTypeMetadata?.courseTypeMetadata?.__typename;
        if (
          courseType &&
          !(
            filterState[productFilterKeys.LEARNING_PRODUCT] &&
            filterState[productFilterKeys.LEARNING_PRODUCT] !== courseType
          )
        ) {
          dropdownOptions[productFilterKeys.LEARNING_PRODUCT][courseType] = getReadableCourseType(courseType);
        }
        // Add an option for each unique domain
        (course.domainTypes || []).forEach((domainType) => {
          // If we are filtering on a domain and this domain is not the one we are filtering on, skip it
          if (!domainType) return;

          if (
            !(filterState[productFilterKeys.SUBJECT] && filterState[productFilterKeys.SUBJECT] !== domainType.domainId)
          ) {
            dropdownOptions[productFilterKeys.SUBJECT][domainType.domainId] = mapDomainIdToReadableName(
              domainType.domainId
            );
          }
        });

        // If not a course the product is an s12n
      } else {
        const s12n = product.product as S12nElement;

        // Add an option for each unique primary language
        // First aggregate the languages from all the courses in the s12n
        const languagesForS12n = (s12n.courses?.elements || []).reduce((languages, course) => {
          // @ts-expect-error string vs maybe string
          languages.push(...[...(course?.primaryLanguages || [])]);
          return languages;
        }, [] as string[]);

        // Then add each unique language to the dropdown options
        languagesForS12n.forEach((language) => {
          // If we are filtering on a language and this language is not the one we are filtering on, skip it
          if (!(filterState[productFilterKeys.LANGUAGE] && filterState[productFilterKeys.LANGUAGE] !== language)) {
            dropdownOptions[productFilterKeys.LANGUAGE][language] = languageCodeToName(language);
          }
        });

        // Add an option for each unique level
        // If its an active filter only add the level which is active
        if (
          s12n?.metadata?.level &&
          !(filterState[productFilterKeys.LEVEL] && filterState[productFilterKeys.LEVEL] !== s12n?.metadata?.level)
        ) {
          dropdownOptions[productFilterKeys.LEVEL][s12n.metadata.level] = mapLevelToReadableName(s12n.metadata.level);
        }

        // Add an option for each unique partner
        // If its an active filter only add the partner which is active
        (s12n.partners?.elements || []).forEach((partner) => {
          if (
            partner &&
            !(filterState[productFilterKeys.PARTNER] && filterState[productFilterKeys.PARTNER] !== partner.id)
          ) {
            dropdownOptions[productFilterKeys.PARTNER][partner.id] = partner.name;
          }
        });

        // Add an option for each unique product type
        // If its an active filter only add the product type which is active
        if (
          s12n.productVariant &&
          !(
            filterState[productFilterKeys.LEARNING_PRODUCT] &&
            filterState[productFilterKeys.LEARNING_PRODUCT] !== s12n.productVariant
          )
        ) {
          dropdownOptions[productFilterKeys.LEARNING_PRODUCT][s12n.productVariant] = getReadableCourseType(
            s12n.productVariant
          );
        }

        (s12n.domainTypes || []).forEach((domainType) => {
          // If we are filtering on a domain and this domain is not the one we are filtering on, skip it
          if (!domainType) return;

          if (
            !(filterState[productFilterKeys.SUBJECT] && filterState[productFilterKeys.SUBJECT] !== domainType.domainId)
          ) {
            dropdownOptions[productFilterKeys.SUBJECT][domainType.domainId] = mapDomainIdToReadableName(
              domainType.domainId
            );
          }
        });
        /* eslint-enable no-param-reassign */
      }

      return dropdownOptions;
    },
    {
      [productFilterKeys.LANGUAGE]: {} as Record<string, string>,
      [productFilterKeys.LEVEL]: {} as Record<string, string>,
      [productFilterKeys.PARTNER]: {} as Record<string, string>,
      [productFilterKeys.LEARNING_PRODUCT]: {} as Record<string, string>,
      [productFilterKeys.SUBJECT]: {} as Record<string, string>,
    }
  );

  return {
    [productFilterKeys.LANGUAGE]: Object.entries(filterDropdownOptionsMap[productFilterKeys.LANGUAGE]).map(
      mapToDropdownOption
    ),
    [productFilterKeys.LEVEL]: Object.entries(filterDropdownOptionsMap[productFilterKeys.LEVEL]).map(
      mapToDropdownOption
    ),
    [productFilterKeys.PARTNER]: Object.entries(filterDropdownOptionsMap[productFilterKeys.PARTNER]).map(
      mapToDropdownOption
    ),
    [productFilterKeys.LEARNING_PRODUCT]: Object.entries(
      filterDropdownOptionsMap[productFilterKeys.LEARNING_PRODUCT]
    ).map(mapToDropdownOption),
    [productFilterKeys.SUBJECT]: Object.entries(filterDropdownOptionsMap[productFilterKeys.SUBJECT]).map(
      mapToDropdownOption
    ),
  };
};

export default generateFilterDropdownOptions;
